import React, { useEffect, useState } from 'react'
import { FiImage } from 'react-icons/fi'
import { imageService } from '@/utils/imageService'
import LoadingSpinner from './LoadingSpinner'

interface GigImageProps {
    imageId?: string | null
    imageData?: any // Pre-fetched image data to avoid extra API calls
    alt?: string
    className?: string
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
    showBorder?: boolean
    showLoadingSpinner?: boolean
    fallbackIcon?: React.ReactNode
    onClick?: () => void
}

const useGigImage = (imageId?: string | null, imageData?: any) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        // If we have pre-fetched image data, use it directly
        if (imageData && imageData.dataUrl) {
            setImageUrl(imageData.dataUrl)
            setLoading(false)
            setError(null)
            return
        }

        if (!imageId) {
            setImageUrl(null)
            setLoading(false)
            setError(null)
            return
        }

        let isCancelled = false

        const fetchImage = async () => {
            try {
                setLoading(true)
                setError(null)

                const fetchedImageData = await imageService.getImageById(imageId)

                if (!isCancelled && fetchedImageData?.dataUrl) {
                    setImageUrl(fetchedImageData.dataUrl)
                } else if (!isCancelled) {
                    setError('Image not found')
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error('Failed to load gig image:', err)
                    setError('Failed to load image')
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false)
                }
            }
        }

        fetchImage()

        return () => {
            isCancelled = true
        }
    }, [imageId, imageData])

    return { imageUrl, loading, error }
}

const GigImage: React.FC<GigImageProps> = ({
    imageId,
    imageData,
    alt = 'Gig image',
    className = '',
    size = 'md',
    showBorder = true,
    showLoadingSpinner = true,
    fallbackIcon,
    onClick
}) => {
    const { imageUrl, loading, error } = useGigImage(imageId, imageData)

    const sizeClasses = {
        sm: 'w-16 h-16',
        md: 'w-24 h-24',
        lg: 'w-32 h-32',
        xl: 'w-48 h-48',
        full: 'w-full h-48'
    }

    const iconSizes = {
        sm: 'w-6 h-6',
        md: 'w-8 h-8',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16',
        full: 'w-20 h-20'
    }

    const baseClasses = [
        'relative',
        'flex',
        'items-center',
        'justify-center',
        'bg-gray-100',
        'dark:bg-gray-700',
        'overflow-hidden',
        'rounded-lg',
        sizeClasses[size]
    ]

    if (showBorder) {
        baseClasses.push('border', 'border-gray-200', 'dark:border-gray-600')
    }

    if (onClick) {
        baseClasses.push('cursor-pointer', 'hover:bg-gray-200', 'dark:hover:bg-gray-600')
    }

    const containerClassName = `${baseClasses.join(' ')} ${className}`

    // Show loading spinner
    if (loading && showLoadingSpinner) {
        return (
            <div className={containerClassName}>
                <LoadingSpinner size="sm" />
            </div>
        )
    }

    // Show actual image if loaded
    if (imageUrl && !error) {
        return (
            <div className={containerClassName} onClick={onClick}>
                <img
                    src={imageUrl}
                    alt={alt}
                    className="w-full h-full object-cover"
                />
            </div>
        )
    }

    // Show fallback icon or default
    const defaultIcon = <FiImage className={`${iconSizes[size]} text-gray-400 dark:text-gray-500`} />

    return (
        <div className={containerClassName} onClick={onClick}>
            {fallbackIcon || defaultIcon}
        </div>
    )
}

export default GigImage